import React from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

/**
 * KPI / stat tile — the single shared version of the `KpiTile` that was duplicated across the
 * admin dashboard, reports, interior-executions and interior-designers pages. White rounded-2xl
 * card, tinted icon badge, big tabular value.
 *
 * Props:
 *  - icon:     lucide icon component
 *  - label:    string (uppercase caption)
 *  - value:    node (the big number)
 *  - subtitle: string (small hint under the value)
 *  - accent:   'flamingo' | 'slate' | 'emerald' | 'indigo' | 'amber' | 'rose'
 *  - loading:  boolean (renders a skeleton)
 *  - dense:    boolean (compact horizontal layout for tight stat rows, e.g. 7-across)
 */
const ACCENTS = {
  flamingo: "bg-flamingo-50 text-flamingo-600",
  slate: "bg-slate-100 text-slate-700",
  emerald: "bg-emerald-50 text-emerald-600",
  indigo: "bg-indigo-50 text-indigo-600",
  amber: "bg-amber-50 text-amber-700",
  rose: "bg-rose-50 text-rose-600",
};

const StatTile = ({ icon: Icon, label, value, subtitle, accent = "slate", loading = false, dense = false }) => {
  const accentCls = ACCENTS[accent] || ACCENTS.slate;

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm ${dense ? "p-4 h-20" : "p-5 h-32"}`}>
        <Skeleton circle height={dense ? 32 : 40} width={dense ? 32 : 40} />
        <Skeleton height={12} width="70%" className="mt-3" />
        {!dense && <Skeleton height={28} width="50%" className="mt-2" />}
      </div>
    );
  }

  if (dense) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${accentCls}`}>
          {Icon && <Icon size={17} />}
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium leading-tight">{label}</p>
          <p className="text-lg font-bold text-slate-900 tabular-nums leading-tight mt-0.5">{value}</p>
          {subtitle && <p className="text-[11px] text-slate-500 leading-tight mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accentCls}`}>
        {Icon && <Icon size={20} />}
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
};

export default StatTile;
